import React, { useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import * as yup from 'yup'

import Button from '@/common/components/Button'
import FileField from '@/common/components/FileField'
import Form from '@/common/components/Form'
import { withPermissionGuard } from '@/common/hocs/withPermissionGuard'
import useApis from '@/common/hooks/useApis'
import useI18n from '@/common/hooks/useI18n'
import useLoadingOverlay from '@/common/hooks/useLoadingOverlay'
import useCurrentUser from '@/modules/authentication/hooks/useCurrentUser'
import { ME_OWNER_PROFILE_PATH } from '@/modules/me/paths'
import registerImage from '@/modules/owner/images/register.svg'
import { OWNER_VERIFY_PATH } from '@/modules/owner/paths'

import { StyledContainer, StyledImage } from './styled'
import { TITLE, DESCRIPTION, SUBMIT } from './translation'

const schema = yup.object().shape({
  document: yup.mixed().required(),
})

const OwnerRegisterPage = () => {
  const { t } = useI18n()
  const { apis } = useApis()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const loadingOverlay = useLoadingOverlay()
  const { data: currentUser, refetch: refetchCurrentUser } = useCurrentUser()

  const redirectTo = searchParams.get('r') ?? ME_OWNER_PROFILE_PATH

  const onSubmit = async values => {
    loadingOverlay.open()

    apis.owner
      .register(values)
      .then(async () => {
        await refetchCurrentUser()
      })
      .finally(() => loadingOverlay.close())
  }

  useEffect(() => {
    if (!currentUser!.Owner) return

    if (!currentUser!.Owner.isApproved) {
      navigate(OWNER_VERIFY_PATH, { replace: true })
    } else {
      navigate(redirectTo, { replace: true })
    }
  })

  if (currentUser!.Owner) return null

  return (
    <div className='flex flex-1 align-center justify-center'>
      <StyledContainer>
        <div className='text-center mb-32'>
          <h1 className='text-24 text-20-mobile text-semibold'>{t(TITLE)}</h1>
          <StyledImage src={registerImage} alt='owner-register' />
          <div className='text-14 text-medium'>{t(DESCRIPTION)}</div>
        </div>
        <Form schema={schema} onSubmit={onSubmit}>
          {({ formState: { isSubmitting } }) => (
            <>
              <FileField name='document' types={['pdf', 'image']} />
              <Button
                className='mt-40 mx-auto'
                type='submit'
                variant='gradient'
                disabled={isSubmitting}
              >
                {t(SUBMIT)}
              </Button>
            </>
          )}
        </Form>
      </StyledContainer>
    </div>
  )
}

export default withPermissionGuard(OwnerRegisterPage, 'member')
